import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { ModalSkeleton } from "../../../components/Dashboard/SkeletonLoaders";
import {
  getInsuranceProtectLandlordDetails,
  runInsuranceCaseAction,
} from "../../../context/functions/AdminFunction";
import { toast } from "react-toastify";
import { RefreshCw, X } from "lucide-react";
import { constants } from "../../../utils/constants";
import FilterDropdown from "./FilterDropdown";
import InsuranceProtectStats from "./InsuranceProtectStats";
import InsuranceProtectTable, {
  CoverageReviewForm,
  IssuePolicyForm,
} from "./InsuranceProtectTable";

const LIMIT = constants.LISTING_ROWLIMIT;

const PRODUCT_OPTIONS = [
  { value: "all", label: "All" },
  { value: "insurance", label: "Insurance Project" },
  { value: "skor_protect", label: "Skor Protect" },
];

const PAYMENT_OPTIONS = [
  { value: "all", label: "All" },
  { value: "paid", label: "Paid" },
  { value: "awaiting_payment", label: "Awaiting payment" },
  { value: "expired", label: "Expired" },
  { value: "refunded", label: "Refunded" },
];

const INSURANCE_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "under_review", label: "Under review" },
  { value: "pending_contract", label: "Pending contract" },
  { value: "processing", label: "Issuing" },
  { value: "active", label: "Active" },
  { value: "rejected", label: "Rejected" },
  { value: "awaiting_payment", label: "Awaiting payment" },
  { value: "not_started", label: "Not started" },
  { value: "failed", label: "Issuing failed" },
  { value: "expired", label: "Expired" },
  { value: "coverage_review", label: "Coverage review" },
];

const ACTION_SUCCESS_MESSAGES = {
  reject: "Case rejected. The landlord has been emailed.",
  refund: "Refund marked. The payer has been emailed.",
  "send-reminder": "Reminder sent to the landlord.",
  "retry-issuance": "Policy issued. The landlord has been emailed and the card shows Active.",
};

const EMPTY_STATS = {
  totalPolicies: 0,
  paid: 0,
  awaitingPayment: 0,
  totalBilled: 0,
  needsOpsAction: 0,
};

const InsuranceProtectDetailsModal = ({
  show,
  onHide,
  selectedLandlord,
  initialNeedsOpsFilter = false,
}) => {
  const [workspaceFilter, setWorkspaceFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [insuranceStatusFilter, setInsuranceStatusFilter] = useState("all");
  const [needsOpsFilter, setNeedsOpsFilter] = useState(false);

  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [landlordInfo, setLandlordInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [reviewItem, setReviewItem] = useState(null);
  const [issueItem, setIssueItem] = useState(null);
  const [busyRowId, setBusyRowId] = useState(null);

  const [workspaceOptions, setWorkspaceOptions] = useState([]);

  const abortRef = useRef(null);
  const formItem = reviewItem || issueItem;

  const fetchDetails = useCallback(
    async ({
      workspaceId,
      product,
      payment,
      insuranceStatus,
      needsOpsAction,
      page,
    }) => {
      const landlordId =
        selectedLandlord?.landlord_id || selectedLandlord?.landlord?._id;
      if (!landlordId) return;

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      try {
        const res = await getInsuranceProtectLandlordDetails({
          landlordId,
          workspaceId: workspaceId === "all" ? "" : workspaceId,
          product,
          payment,
          insuranceStatus,
          needsOpsAction,
          page,
          limit: LIMIT,
          signal: controller.signal,
        });

        if (!res || res.status === "canceled") return;

        if (res.success && res.success_res) {
          setList(res.success_res.data || []);
          setTotal(res.success_res.total || 0);
          setStats({ ...EMPTY_STATS, ...(res.success_res.stats || {}) });
          setLandlordInfo(res.success_res.landlord || null);
          if (res.success_res.workspaces?.length)
            setWorkspaceOptions(res.success_res.workspaces);
        } else {
          setList([]);
          setTotal(0);
          setStats(EMPTY_STATS);
        }
      } catch (err) {
        if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
          toast.error("Failed to load insurance & Skor Protect details");
        }
      } finally {
        // A cancelled request must not end the loading of the newer one.
        if (abortRef.current === controller) setLoading(false);
      }
    },
    [selectedLandlord]
  );

  const currentQuery = {
    workspaceId: workspaceFilter,
    product: productFilter,
    payment: paymentFilter,
    insuranceStatus: insuranceStatusFilter,
    needsOpsAction: needsOpsFilter,
    page: currentPage,
  };

  useEffect(() => {
    if (!show) return;
    fetchDetails(currentQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    show,
    workspaceFilter,
    productFilter,
    paymentFilter,
    insuranceStatusFilter,
    needsOpsFilter,
    currentPage,
    fetchDetails,
  ]);

  useEffect(() => {
    if (show) {
      setWorkspaceFilter("all");
      setProductFilter("all");
      setPaymentFilter("all");
      setInsuranceStatusFilter("all");
      setNeedsOpsFilter(Boolean(initialNeedsOpsFilter));
      setLoading(true);
      setCurrentPage(1);
      setList([]);
      setWorkspaceOptions([]);
      setStats(EMPTY_STATS);
      setLandlordInfo(null);
      setReviewItem(null);
      setIssueItem(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, selectedLandlord?.landlord_id]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const closeForm = () => {
    setReviewItem(null);
    setIssueItem(null);
  };

  const handleHide = () => {
    if (abortRef.current) abortRef.current.abort();
    setReviewItem(null);
    setIssueItem(null);
    onHide();
  };

  const refreshCurrentPage = () => fetchDetails(currentQuery);

  const handleFormDone = async () => {
    closeForm();
    await refreshCurrentPage();
  };

  const handleCaseAction = async (item, action) => {
    if (!item?.property?._id) return;
    setBusyRowId(item._id);
    try {
      await runInsuranceCaseAction({ propertyId: item.property._id, action });
      toast.success(ACTION_SUCCESS_MESSAGES[action] || "Done");
      await refreshCurrentPage();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Action failed"
      );
    } finally {
      setBusyRowId(null);
    }
  };

  const resetPageAnd = (setter) => (value) => {
    setCurrentPage(1);
    setter(value);
  };

  const workspaceDropdownOptions = [
    { value: "all", label: "All" },
    ...workspaceOptions.map((ws) => ({ value: ws._id, label: ws.name })),
  ];
  const landlordName =
    [
      selectedLandlord?.landlord?.fname_companyName,
      selectedLandlord?.landlord?.lname,
    ]
      .filter(Boolean)
      .join(" ") || landlordInfo?.name;
  const landlordEmail = selectedLandlord?.landlord?.email || landlordInfo?.email;

  return (
    <>
      <Modal
        show={show}
        onHide={handleHide}
        size="xl"
        dialogClassName="modal-90w"
        centered
        scrollable
        className="landlord-metrics-modal"
      >
        <Modal.Header className="landlord-details-header">
          <div className="d-flex justify-content-between align-items-start w-100">
            <div>
              <h4 className="landlord-details-name">{landlordName}</h4>
              <p className="landlord-details-email">
                {landlordEmail || "No email provided"}
              </p>
            </div>
            <button className="modal-close-btn" onClick={handleHide}>
              <X size={20} />
            </button>
          </div>
        </Modal.Header>

        <Modal.Body className="px-5 pb-5 pt-0">
          <div className="ip-filters-row mt-4 mb-4">
            <FilterDropdown
              label="Workspaces"
              value={workspaceFilter}
              options={workspaceDropdownOptions}
              onChange={resetPageAnd(setWorkspaceFilter)}
            />
            <FilterDropdown
              label="Product"
              value={productFilter}
              options={PRODUCT_OPTIONS}
              onChange={resetPageAnd(setProductFilter)}
            />
            <FilterDropdown
              label="Payment"
              value={paymentFilter}
              options={PAYMENT_OPTIONS}
              onChange={resetPageAnd(setPaymentFilter)}
            />
            <FilterDropdown
              label="Insurance status"
              value={insuranceStatusFilter}
              options={INSURANCE_STATUS_OPTIONS}
              onChange={resetPageAnd(setInsuranceStatusFilter)}
            />
            <button
              type="button"
              className={`ip-filter-chip ${needsOpsFilter ? "active" : ""}`}
              onClick={() => resetPageAnd(setNeedsOpsFilter)(!needsOpsFilter)}
            >
              Needs ops action &middot;{" "}
              {loading ? "…" : stats.needsOpsAction}
            </button>
            <button
              type="button"
              className="ip-refresh-btn"
              onClick={refreshCurrentPage}
              disabled={loading}
              title="Refresh"
            >
              <RefreshCw
                size={14}
                className={loading ? "ip-refresh-spin" : undefined}
              />
              Refresh
            </button>
          </div>

          <div className="mb-4">
            <InsuranceProtectStats
              loading={loading}
              totalPolicies={stats.totalPolicies}
              paid={stats.paid}
              awaitingPayment={stats.awaitingPayment}
              totalBilled={stats.totalBilled}
              needsOpsAction={stats.needsOpsAction}
            />
          </div>

          <p className="ip-table-caption mb-3">
            All insurances and Skor Protect policies for this landlord across
            workspaces &middot; use filters to narrow billing
          </p>

          {loading ? (
            <ModalSkeleton />
          ) : (
            <InsuranceProtectTable
              data={list}
              total={total}
              currentPage={currentPage}
              limit={LIMIT}
              onPageChange={setCurrentPage}
              onReviewRequested={setReviewItem}
              onIssueRequested={setIssueItem}
              onCaseAction={handleCaseAction}
              busyRowId={busyRowId}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Upload policy / Resolve review: on top of the case list, which stays
          open behind a blurred backdrop. */}
      <Modal
        show={show && Boolean(formItem)}
        onHide={closeForm}
        dialogClassName="ip-upload-policy-dialog"
        centered
        scrollable
        className="landlord-metrics-modal ip-confirm-modal"
        backdropClassName="ip-confirm-backdrop"
      >
        <Modal.Body className="p-0">
          {issueItem ? (
            <IssuePolicyForm
              item={issueItem}
              landlordName={landlordName}
              onCancel={closeForm}
              onIssued={handleFormDone}
            />
          ) : (
            reviewItem && (
              <CoverageReviewForm
                item={reviewItem}
                landlordName={landlordName}
                onCancel={closeForm}
                onResolved={handleFormDone}
              />
            )
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default InsuranceProtectDetailsModal;
